import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { DataSource } from '@angular/cdk/table'
import { ErrorHandler, Injectable } from '@angular/core'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'

import { BehaviorSubject, Observable, combineLatest, of as observableOf } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

import { FirestoreTosite, PaymentStatus } from '@lemon/core-firebase/model/tosite'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'

interface Hakutiedot {
  tila: PaymentStatus | 'kaikki'
  sivutustiedot: PageEvent
  lajittelutiedot: Sort
}

/**
 * Read-only datasource lemonator-puolen ostolaskujen selaukselle. Kysyy /customers/{asiakasAvain}/tositteet
 * Lemonaid-firestoresta ja suodattaa client-puolella ostolasku-tyyppiset rivit (sähköiset laskut
 * ja maksut). Tavalliset käteistositteet (type='tosite') jätetään ulos koska niillä ei ole
 * ostolaskuihin liittyvää tilaa/eräpäivää.
 */
@Injectable()
export class OstolaskutSelaaDataSourceService extends DataSource<FirestoreTosite> {

  private _hakutiedot: Hakutiedot = {
    tila: 'kaikki',
    sivutustiedot: { length: 0, pageIndex: 0, pageSize: 20 },
    lajittelutiedot: { active: 'dueDate', direction: 'desc' },
  }
  private _hakutiedotSubject = new BehaviorSubject<Hakutiedot>(this._hakutiedot)
  hakutiedotObservable: Observable<Hakutiedot> = this._hakutiedotSubject.asObservable()

  private _lataaSubject = new BehaviorSubject<boolean>(true)
  lataaObservable: Observable<boolean> = this._lataaSubject.asObservable()

  private _rivienMaaraSubject = new BehaviorSubject<number>(0)
  rivienMaaraObservable: Observable<number> = this._rivienMaaraSubject.asObservable()

  private _kaikkiObservable: Observable<FirestoreTosite[]>
  nakyvatObservable: Observable<FirestoreTosite[]>

  constructor(
    private _errorHandler: ErrorHandler,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _asiakasService: AsiakasService,
    private _tositeUriService: TositeUriService,
  ) {
    super()

    this._kaikkiObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      tap(() => this._lataaSubject.next(true)),
      switchMap(avainTiedot => {
        if (!avainTiedot?.avain) {
          return observableOf<FirestoreTosite[]>([])
        }
        const uri = this._tositeUriService.annaTositteidenFirestoreCollectionUri(avainTiedot.avain)
        return this._firebaseLemonaid.firestoreCollection<FirestoreTosite>(uri).listenSnapshots().pipe(
          map(snapshots => snapshots
            .map(snap => {
              const t = snap.data()
              t.avain = snap.id
              return t
            })
            .filter(t => t.type === 'einvoice' || t.type === 'payment')),
        )
      }),
      tap(() => this._lataaSubject.next(false)),
      lemonShare(),
    )

    this.nakyvatObservable = combineLatest([this._kaikkiObservable, this._hakutiedotSubject]).pipe(
      map(([rivit, hakutiedot]) => {
        let suodatetut = rivit.filter(r => !r.poistettu)

        if (hakutiedot.tila !== 'kaikki') {
          suodatetut = suodatetut.filter(r => r.paymentStatus === hakutiedot.tila)
        }

        const { active, direction } = hakutiedot.lajittelutiedot
        if (active && direction) {
          const suunta = direction === 'asc' ? 1 : -1
          suodatetut = suodatetut.slice().sort((a, b) => this._vertaa(a, b, active) * suunta)
        }

        this._rivienMaaraSubject.next(suodatetut.length)

        const { pageIndex, pageSize } = hakutiedot.sivutustiedot
        const alku = pageIndex * pageSize
        return suodatetut.slice(alku, alku + pageSize)
      }),
      lemonShare(),
    )
  }

  private _vertaa(a: FirestoreTosite, b: FirestoreTosite, kentta: string): number {
    if (kentta === 'sum' || kentta === 'summa') {
      return (a.sum ?? a.summa ?? 0) - (b.sum ?? b.summa ?? 0)
    }
    if (kentta === 'dueDate' || kentta === 'p' || kentta === 'paymentDate' || kentta === 'invoiceDate') {
      const numA = (a as unknown as Record<string, number>)[kentta] ?? 0
      const numB = (b as unknown as Record<string, number>)[kentta] ?? 0
      return numA - numB
    }
    const tekstiA = String((a as unknown as Record<string, unknown>)[kentta] ?? '').toLowerCase()
    const tekstiB = String((b as unknown as Record<string, unknown>)[kentta] ?? '').toLowerCase()
    return tekstiA.localeCompare(tekstiB)
  }

  setTila(tila: PaymentStatus | 'kaikki') {
    this._hakutiedot = { ...this._hakutiedot, tila, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  setSort(sort: Sort) {
    this._hakutiedot = { ...this._hakutiedot, lajittelutiedot: sort, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  setPage(page: PageEvent) {
    this._hakutiedot = { ...this._hakutiedot, sivutustiedot: page }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  get sort(): Sort {
    return this._hakutiedot.lajittelutiedot
  }

  connect(): Observable<FirestoreTosite[]> {
    return this.nakyvatObservable
  }

  disconnect() {}
}
